import { ColDef, ColumnApi, ColumnState } from '@ag-grid-community/core'
import { countFilters } from '@helios/server-side-util'
import { GridState } from '@helios/state-management'
import { AgGridApi } from '@northfork/ag-grid'
import { AgGridState } from '@northfork/ag-grid-angular'
import * as _ from 'lodash-es'

export const cacheLoadingFilterTooltip =
  'Filtering is currently disabled until all data is loaded'

export const cacheLoadingGeneralTooltip =
  'Some Functionality is currently disabled until all data is loaded'

export const cacheLoadingErrorTooltip =
  'Some Functionality is currently disabled the panel is reloaded'

/**
 * Header shown by the cache-load-listener when a panel is not applicable for
 * the active session. Domain-neutral on purpose - the body copy carries the
 * reason, this is the single shared header for every consumer.
 */
export const notApplicableHeader = 'Not Applicable'

/**
 * Body copy shown in the Positions panel when the active session was created
 * from Enfusion-sourced accounts.
 */
export const positionsNotApplicableMessage =
  'Not applicable for new enfusion accounts use the new Position 2.0 panel'

/**
 * Body copy shown in both Transactions children (Clearwater Transactions and
 * External Transactions) when the active session was created from
 * Enfusion-sourced accounts.
 */
export const transactionsNotApplicableMessage =
  'Not applicable for new enfusion accounts use the new Transactions 2.0 panel'

/**
 * disable cache dependent features
 */
export function disableCacheDependentFeatures<T extends ColDef>(
  colDefs: T[],
): T[] {
  return colDefs.map(colDef => {
    return {
      ...colDef,
      sortable: false,
      enableRowGroup: false,
      headerClass: 'cache-loading',
    }
  })
}

/**
 * enable cache dependent features
 */
export function enableCacheDependentFeatures(
  currColDefs: ColDef[],
  baseColDefs: ColDef[],
): ColDef[] {
  return currColDefs.map(def => {
    const matchingDef = baseColDefs.find(
      baseDef => columnKey(baseDef) === columnKey(def),
    )
    if (matchingDef == null) return def
    return {
      ...def,
      sortable: matchingDef.sortable,
      filter: matchingDef.filter,
      enableRowGroup: matchingDef.enableRowGroup,
      menuTabs: matchingDef.menuTabs,
      headerClass: matchingDef.headerClass,
    }
  })
}

// A column without a field, such as Comments, is known only by its colId.
function columnKey(colDef: ColDef): string | undefined {
  return colDef.colId ?? colDef.field
}

/**
 * Returns a set of column definitions based on grid state (pre or post onGridReady) and cache state (loading vs done)
 *
 * @param loaded - is cache loaded?
 * @param baseColDefs - The col defs from the basic configuration
 * @param api - the ag grid api
 */
export function getColDefs(
  loaded: boolean,
  baseColDefs: ColDef[],
  api?: AgGridApi,
): ColDef[] {
  const currColDefs: ColDef[] | undefined = api?.api.getColumnDefs()

  if (!loaded) return disableCacheDependentFeatures(currColDefs ?? baseColDefs)
  if (currColDefs == null) return baseColDefs
  return enableCacheDependentFeatures(currColDefs, baseColDefs)
}

/**
 * Update column defs
 */
export function updateColDefs<T extends ColDef>(
  colDefs: T[],
  agGridApi?: AgGridApi,
): void {
  if (agGridApi == null) return
  const { api, columnApi } = agGridApi

  if (_.isEqual(colDefs, api.getColumnDefs())) return

  api.setColumnDefs(colDefs)
  api.refreshHeader()

  if ((countFilters(api) ?? 0) > 0) api.onFilterChanged()
  else if (isSortApplied(columnApi)) {
    api.onSortChanged()
  }
}

/**
 *
 */
function isSortApplied(columnApi: ColumnApi): boolean {
  return columnApi.getColumnState().some(cs => cs.sort != null)
}

/**
 * get grid state
 */
export function getGridState(api?: AgGridApi): GridState | undefined {
  if (api == null) return undefined

  const columnState = api.columnApi.getColumnState()
  const columnGroupState = api.columnApi.getColumnGroupState()
  const filterModel = api.api.getFilterModel()

  return {
    columnState,
    columnGroupState,
    filterModel,
  }
}

/**
 * After the cache loads, we want to apply the original grid state (sorting, filters, etc)
 */
export function restoreGridState(state?: AgGridState, api?: AgGridApi) {
  if (api == null) return

  if (state == null) return resetGridState(api)

  const { columnState, columnGroupState, filterState } = state

  if (columnState == null) api.columnApi.resetColumnState()
  else
    api.columnApi.applyColumnState({
      state: columnState,
      applyOrder: true,
    })

  if (columnGroupState == null) api.columnApi.resetColumnGroupState()
  else api.columnApi.setColumnGroupState(columnGroupState)

  // apply filter model when it has any values in it
  if (Object.keys(filterState ?? {}).length) {
    api.api.setFilterModel(filterState)
  }
}

/**
 * reset grid state
 */
function resetGridState(api?: AgGridApi) {
  if (api == null) return

  api.columnApi.resetColumnState()
  api.columnApi.resetColumnGroupState()
  api.api.setFilterModel(null)
}

/**
 * reset server side store
 */
export function resetServerSideStore(api?: AgGridApi) {
  if (api == null) return
  api.api.setFilterModel(null)
  api.api.refreshServerSideStore({ purge: true })
}

/**
 * The order of the column state dictates the order of the columns in the grid.
 *
 * @param baseColDefs
 * @param columnState
 */
export function mergeBaseColDefsWithColumnState(
  baseColDefs: ColDef[],
  columnState?: ColumnState[],
): ColDef[] {
  if (columnState == null) {
    return baseColDefs
  }

  const [unmatchedColDefs, matchedColDefsById] = baseColDefs.reduce<
    [ColDef[], Map<string | undefined, ColDef>]
  >(
    (colDefs, baseColDef) => {
      const baseColDefColId = baseColDef.colId ?? baseColDef.field
      if (colDefs[1].has(baseColDefColId)) {
        console.error(
          `Base column defs have duplicate colId/field, ${baseColDefColId}.
           Col Id either needs to be made unique or specified.`,
        )
        colDefs[0].push(baseColDef)
      } else if (
        columnState.some(savedColDef => baseColDefColId === savedColDef.colId)
      ) {
        colDefs[1].set(baseColDefColId, baseColDef)
      } else {
        colDefs[0].push(baseColDef)
      }
      return colDefs
    },
    [[], new Map()],
  )

  const mergedColDefs: ColDef[] = columnState
    .map(savedColDef => {
      if (matchedColDefsById.has(savedColDef.colId)) {
        return {
          ...matchedColDefsById.get(savedColDef.colId),
          ...savedColDef,
        } as ColDef
      } else {
        //It doesn't make sense to merge these as they were either removed from the grid or you will end up with columns without headers.
        return undefined
      }
    })
    .filter((savedColDef): savedColDef is ColDef => savedColDef != null)

  return mergedColDefs.concat(unmatchedColDefs)
}
